/// <amd-module name="Wit/Common/Utils/WorkItemUtils/FetchWorkItemsDialog" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { ProgressBar } from "VSSUI/ProgressBar";
import { Spinner, SpinnerOrientation, SpinnerSize } from "VSSUI/Spinner";
import { executeCallbackForTargetIdsInBatches } from "Wit/Common/WorkItemDialogs/Components/BatchExecutionHelper";
import { PageSizes } from "Wit/WorkItemModel/OM";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";

export interface IFetchWorkItemsDialogProps {
    workItemIds: number[];
    onDismiss: () => void;
    callBack?: () => void;
    processWorkItems: (workItemIds: number[]) => Promise<boolean>;
    maximumAmountOfSimultaneousRequests?: number;
}

interface IFetchWorkItemsDialogState {
    errorMessage: string | undefined;
    operationStatus: FetchWorkItemsDialogOperationStatus;
}

enum FetchWorkItemsDialogOperationStatus {
    /**
     * The operation is waiting in a queue and has not yet started.
     */
    Queued = 1,
    /**
     * The operation is currently in progress.
     */
    InProgress = 2,
    /**
     * The operation has completed.
     */
    Completed = 3,
    /**
     * The operation has failed. Check for an error message.
     */
    Failed = 4
}

export class FetchWorkItemsDialog extends VssComponent<IFetchWorkItemsDialogProps, IFetchWorkItemsDialogState> {
    private stepNumber = new ObservableValue<number>(0);
    private stepCount = new ObservableValue<number>(0);
    private failedIds: number[] | undefined = undefined;
    private defaultMaximumAmountOfSimultaneousRequests = 5;

    public constructor(props: IFetchWorkItemsDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorMessage: undefined,
            operationStatus: FetchWorkItemsDialogOperationStatus.Queued
        };
    }

    public async componentDidMount(): Promise<void> {
        this.setState({ operationStatus: FetchWorkItemsDialogOperationStatus.InProgress });
        try {
            const succeeded = await executeCallbackForTargetIdsInBatches(
                this.props.workItemIds,
                subsetTargetIds => this.fetchWorkItemsUsingBatches(subsetTargetIds),
                this.stepCount,
                this.stepNumber,
                this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests
            );

            if (succeeded) {
                this.setState({ operationStatus: FetchWorkItemsDialogOperationStatus.Completed });
                this.props.callBack?.();
                this.props.onDismiss();
            } else {
                this.setState({ operationStatus: FetchWorkItemsDialogOperationStatus.Failed });
            }
        } catch (error) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...this.props.workItemIds])];
            this.setState({ operationStatus: FetchWorkItemsDialogOperationStatus.Failed, errorMessage: error.message });
        }
    }

    private fetchWorkItemsUsingBatches = async (workItemIds: number[]): Promise<boolean> => {
        try {
            return this.props.processWorkItems(workItemIds);
        } catch (error) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...workItemIds])];
            this.setState({ operationStatus: FetchWorkItemsDialogOperationStatus.Failed, errorMessage: error.message });
            return false;
        }
    };

    public render() {
        return (
            <Observer stepNumber={this.stepNumber} stepCount={this.stepCount}>
                {(observableProps: { stepNumber: number; stepCount: number }) => {
                    const { title, content } = this.getContent(observableProps);

                    return (
                        <Dialog
                            titleProps={{ text: title }}
                            onDismiss={this.props.onDismiss}
                            escDismiss={true}
                            lightDismiss={false}
                            footerButtonProps={[
                                {
                                    text: Resources.OK,
                                    onClick: this.props.onDismiss,
                                    primary: true,
                                    disabled: this.state.operationStatus === FetchWorkItemsDialogOperationStatus.InProgress
                                }
                            ]}
                        >
                            {content}
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private getContent(observableProps: { stepNumber: number; stepCount: number; }) {
        let title = Resources.FetchingWorkItemsTitle;
        let content = <Spinner className="padding-vertical-8" size={SpinnerSize.large} />;

        switch (this.state.operationStatus) {
            case FetchWorkItemsDialogOperationStatus.InProgress: {
                const processingTemlate = Resources.FetchingWorkItemsProcessingMessage;
                const stepMultiplier = PageSizes.QUERY;
                const from = observableProps.stepNumber * stepMultiplier + 1;
                const requestCount = this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests;
                const to = (observableProps.stepNumber + requestCount) * stepMultiplier;
                const processingMessage = format(
                    processingTemlate,
                    from,
                    to < this.props.workItemIds.length ? to : this.props.workItemIds.length,
                    this.props.workItemIds.length
                );
                content = (
                    <>
                        <ProgressBar
                            className="flex-self-stretch"
                            currentValue={observableProps.stepNumber}
                            maxValue={observableProps.stepCount} />
                        <Spinner
                            className="font-size-ms padding-vertical-16"
                            label={processingMessage}
                            orientation={SpinnerOrientation.row} />
                    </>
                );
                break;
            }
            case FetchWorkItemsDialogOperationStatus.Completed: {
                const successTitleTemplate = Resources.FetchingWorkItemsSucceededTitle;
                const successMessageTemplate = Resources.FetchingWorkItemsSucceededMessage;
                title = successTitleTemplate;
                content = (
                    <div className="scroll-auto flex-grow flex-column">
                        <p>{format(successMessageTemplate, this.props.workItemIds.length)}</p>
                    </div>
                );
                break;
            }
            case FetchWorkItemsDialogOperationStatus.Failed: {
                const errorTitleTemplate = Resources.FetchingWorkItemsFailedTitle;
                const errorMessageTemplate = Resources.FetchingWorkItemsFailedMessage;
                title = errorTitleTemplate;
                content = (
                    <div className="scroll-auto flex-grow flex-column">
                        {this.state.errorMessage && (
                            <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error}>
                                {this.state.errorMessage}
                            </MessageCard>
                        )}
                        <p>
                            {format(
                                errorMessageTemplate,
                                this.failedIds ? this.failedIds.join(Resources.FetchingWorkItemsFailedSeparator) : this.props.workItemIds.join(Resources.FetchingWorkItemsFailedSeparator)
                            )}
                        </p>
                    </div>
                );
                break;
            }
            case FetchWorkItemsDialogOperationStatus.Queued:
            default: {
                break;
            }
        }
        return { title, content };
    }
}
